import * as tf from '@tensorflow/tfjs';
import * as cocoSsd from '@tensorflow-models/coco-ssd';

// COCO-SSD class names mapped to prop categories
const COCO_TO_PROP_MAPPING = {
  // Furniture
  'chair': { category: 'Furniture', materials: ['wood', 'fabric'], tags: ['seating'] },
  'couch': { category: 'Furniture', materials: ['fabric', 'leather'], tags: ['seating', 'upholstered'] },
  'dining table': { category: 'Furniture', materials: ['wood'], tags: ['table', 'dining'] },
  'bed': { category: 'Furniture', materials: ['wood', 'metal'], tags: ['bedroom'] },
  
  // Lighting
  'tv': { category: 'Electronics', materials: ['plastic', 'metal'], tags: ['screen', 'display'] },
  
  // Decorative Objects
  'vase': { category: 'Decorative Objects', materials: ['ceramic', 'glass'], tags: ['container', 'flowers'] },
  'potted plant': { category: 'Decorative Objects', materials: ['ceramic', 'plant'], tags: ['greenery', 'nature'] },
  'clock': { category: 'Decorative Objects', materials: ['wood', 'metal'], tags: ['timepiece'] },
  
  // Kitchen & Dining
  'bowl': { category: 'Kitchen & Dining', materials: ['ceramic', 'wood'], tags: ['container', 'serving'] },
  'cup': { category: 'Kitchen & Dining', materials: ['ceramic', 'glass'], tags: ['drinkware'] },
  'wine glass': { category: 'Kitchen & Dining', materials: ['glass'], tags: ['drinkware', 'formal'] },
  'bottle': { category: 'Kitchen & Dining', materials: ['glass', 'plastic'], tags: ['container'] },
  'banana': { category: 'Kitchen & Dining', materials: ['organic'], tags: ['fruit', 'food prop'] },
  'apple': { category: 'Kitchen & Dining', materials: ['organic'], tags: ['fruit', 'food prop'] },
  'orange': { category: 'Kitchen & Dining', materials: ['organic'], tags: ['fruit', 'food prop'] },
  'cake': { category: 'Kitchen & Dining', materials: ['organic'], tags: ['dessert', 'food prop'] },
  
  // Books & Documents
  'book': { category: 'Books & Documents', materials: ['paper'], tags: ['reading', 'literature'] },
  
  // Sports & Recreation
  'sports ball': { category: 'Sports & Recreation', materials: ['rubber', 'leather'], tags: ['ball', 'game'] },
  'baseball bat': { category: 'Sports & Recreation', materials: ['wood'], tags: ['sport', 'baseball'] },
  'tennis racket': { category: 'Sports & Recreation', materials: ['metal', 'string'], tags: ['sport', 'tennis'] },
  'frisbee': { category: 'Sports & Recreation', materials: ['plastic'], tags: ['game', 'outdoor'] },
  
  // Transportation
  'bicycle': { category: 'Transportation', materials: ['metal'], tags: ['vehicle', 'two-wheel'] },
  'motorcycle': { category: 'Transportation', materials: ['metal'], tags: ['vehicle', 'motor'] },
  'car': { category: 'Transportation', materials: ['metal'], tags: ['vehicle', 'automobile'] },
  'bus': { category: 'Transportation', materials: ['metal'], tags: ['vehicle', 'public transport'] },
  'truck': { category: 'Transportation', materials: ['metal'], tags: ['vehicle', 'commercial'] },
  
  // Animals (for animal props/costumes)
  'cat': { category: 'Animals', materials: ['fabric', 'fur'], tags: ['pet', 'feline'] },
  'dog': { category: 'Animals', materials: ['fabric', 'fur'], tags: ['pet', 'canine'] },
  'bird': { category: 'Animals', materials: ['feathers'], tags: ['flying', 'avian'] },
  'horse': { category: 'Animals', materials: ['fabric'], tags: ['large animal', 'equine'] },
  
  // Clothing & Accessories
  'handbag': { category: 'Clothing & Accessories', materials: ['leather', 'fabric'], tags: ['bag', 'accessory'] },
  'suitcase': { category: 'Clothing & Accessories', materials: ['fabric', 'plastic'], tags: ['luggage', 'travel'] },
  'umbrella': { category: 'Clothing & Accessories', materials: ['fabric', 'metal'], tags: ['weather', 'protection'] },
  'tie': { category: 'Clothing & Accessories', materials: ['silk', 'fabric'], tags: ['formal', 'neckwear'] },
  
  // Tools & Hardware
  'scissors': { category: 'Tools & Hardware', materials: ['metal'], tags: ['cutting', 'sharp'] },
  'hair drier': { category: 'Tools & Hardware', materials: ['plastic'], tags: ['beauty', 'electric'] },
  'toothbrush': { category: 'Tools & Hardware', materials: ['plastic'], tags: ['hygiene', 'personal care'] }
};

// Historical period keywords for enhanced classification
const HISTORICAL_KEYWORDS = {
  'chair': {
    'antique': 'Victorian Era (1837-1901)',
    'wooden': 'Colonial Period (1600s-1700s)', 
    'ornate': 'Baroque Period (1600-1750)',
    'simple': 'Modern Era (1900-present)'
  },
  'table': {
    'carved': 'Renaissance (1400-1600)',
    'elegant': 'Regency Period (1811-1820)',
    'rustic': 'Colonial Period (1600s-1700s)'
  },
  'vase': {
    'ceramic': 'Ancient Period (Before 500 CE)',
    'porcelain': 'Ming Dynasty (1368-1644)',
    'crystal': 'Art Deco (1920-1940)'
  }
};

class AIModelService {
  constructor() {
    this.model = null;
    this.isLoading = false;
  }

  // Load the COCO-SSD model
  async loadModel() {
    if (this.model) return this.model;
    if (this.isLoading) {
      // Wait for current loading to complete
      while (this.isLoading) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      return this.model;
    }

    try {
      this.isLoading = true;
      console.log('Loading COCO-SSD model...');
      
      // Load TensorFlow.js backend
      await tf.ready();
      
      // Load the COCO-SSD model
      this.model = await cocoSsd.load();
      console.log('COCO-SSD model loaded successfully');
      
      return this.model;
    } catch (error) {
      console.error('Error loading COCO-SSD model:', error);
      throw new Error('Failed to load AI model: ' + error.message);
    } finally {
      this.isLoading = false;
    }
  }

  // Analyze a single image
  async analyzeImage(imageElement, imageName = '') {
    try {
      const model = await this.loadModel();
      
      // Run object detection
      const predictions = await model.detect(imageElement);
      
      console.log('Raw predictions for', imageName, ':', predictions);
      
      if (predictions.length === 0) {
        return {
          success: false,
          confidence: 0,
          message: 'No objects detected in image',
          suggestions: []
        };
      }
      
      // Sort by confidence and get best prediction
      const sortedPredictions = predictions.sort((a, b) => b.score - a.score);
      const bestPrediction = sortedPredictions[0];
      
      // Generate prop information
      const propInfo = this.generatePropInfo(bestPrediction, sortedPredictions, imageName);
      
      return {
        success: true,
        confidence: Math.round(bestPrediction.score * 100),
        primaryPrediction: bestPrediction,
        allPredictions: sortedPredictions,
        propInfo: propInfo,
        suggestions: this.generateMultipleSuggestions(sortedPredictions)
      };
      
    } catch (error) {
      console.error('Error analyzing image:', error);
      return {
        success: false,
        confidence: 0,
        message: 'Error analyzing image: ' + error.message,
        suggestions: []
      };
    }
  }

  // Generate prop information from prediction
  generatePropInfo(prediction, allPredictions, imageName) {
    const detectedClass = prediction.class.toLowerCase();
    const confidence = prediction.score;
    
    // Get base prop info from mapping
    const baseInfo = COCO_TO_PROP_MAPPING[detectedClass] || {
      category: 'Miscellaneous',
      materials: ['unknown'],
      tags: ['misc']
    };
    
    // Generate a descriptive name
    const propName = this.generatePropName(detectedClass, imageName, confidence);
    
    // Determine historical period (basic heuristic)
    const historicalPeriod = this.determineHistoricalPeriod(detectedClass, propName);
    
    // Generate additional tags based on context
    const additionalTags = this.generateContextualTags(detectedClass, allPredictions);
    
    return {
      name: propName,
      category: baseInfo.category,
      materials: baseInfo.materials,
      tags: [...baseInfo.tags, ...additionalTags],
      historical_period: historicalPeriod,
      historical_region: 'Unknown - Requires Manual Review',
      verified: false, // AI predictions should be manually verified
      ai_confidence: Math.round(confidence * 100),
      detected_class: detectedClass
    };
  }

  // Generate multiple suggestions with different confidence levels
  generateMultipleSuggestions(predictions) {
    return predictions
      .filter(pred => pred.score > 0.3) // Only suggestions with >30% confidence
      .slice(0, 3) // Top 3 suggestions
      .map(pred => {
        const baseInfo = COCO_TO_PROP_MAPPING[pred.class.toLowerCase()] || {
          category: 'Miscellaneous',
          materials: ['unknown'],
          tags: ['misc']
        };
        
        return {
          name: this.generatePropName(pred.class.toLowerCase(), '', pred.score),
          category: baseInfo.category,
          confidence: Math.round(pred.score * 100),
          detected_class: pred.class.toLowerCase()
        };
      });
  }

  // Generate a descriptive prop name
  generatePropName(detectedClass, imageName, confidence) {
    const baseNames = {
      'chair': ['Wooden Chair', 'Upholstered Chair', 'Dining Chair', 'Accent Chair'],
      'couch': ['Fabric Sofa', 'Leather Couch', 'Sectional Sofa', 'Loveseat'],
      'dining table': ['Wooden Dining Table', 'Round Table', 'Rectangular Table'],
      'vase': ['Decorative Vase', 'Ceramic Vase', 'Glass Vase', 'Flower Vase'],
      'book': ['Vintage Book', 'Hardcover Book', 'Leather-bound Book'],
      'bottle': ['Glass Bottle', 'Vintage Bottle', 'Decorative Bottle'],
      'cup': ['Tea Cup', 'Coffee Mug', 'Ceramic Cup'],
      'bowl': ['Serving Bowl', 'Ceramic Bowl', 'Decorative Bowl']
    };
    
    const names = baseNames[detectedClass] || [this.capitalizeFirst(detectedClass)];
    
    // Pick a name based on confidence (higher confidence = more specific name)
    const nameIndex = Math.min(Math.floor(confidence * names.length), names.length - 1);
    return names[nameIndex];
  }

  // Determine historical period (basic heuristic)
  determineHistoricalPeriod(detectedClass, propName) {
    const keywords = HISTORICAL_KEYWORDS[detectedClass];
    if (!keywords) return 'Modern Era (1900-present)';
    
    const lowerName = propName.toLowerCase();
    for (const [keyword, period] of Object.entries(keywords)) {
      if (lowerName.includes(keyword)) {
        return period;
      }
    }
    
    return 'Modern Era (1900-present)';
  }

  // Generate contextual tags based on other detected objects
  generateContextualTags(primaryClass, allPredictions) {
    const contextualTags = [];
    
    // If multiple objects detected, add context tags
    if (allPredictions.length > 1) {
      contextualTags.push('multi-object scene');
    }
    
    // Add style tags based on object type
    const styleTags = {
      'chair': ['vintage', 'modern', 'antique'],
      'table': ['formal', 'casual', 'rustic'],
      'vase': ['ornate', 'simple', 'decorative'],
      'book': ['literature', 'reference', 'vintage']
    };
    
    const tags = styleTags[primaryClass];
    if (tags) {
      // Add a random style tag for variety
      const randomTag = tags[Math.floor(Math.random() * tags.length)];
      contextualTags.push(randomTag);
    }
    
    return contextualTags;
  }

  // Utility function to capitalize first letter
  capitalizeFirst(str) {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  // Check if model is ready
  isReady() {
    return this.model !== null;
  }

  // Get model info
  getModelInfo() {
    return {
      name: 'COCO-SSD',
      description: 'Common Objects in Context - Single Shot Detection',
      classes: Object.keys(COCO_TO_PROP_MAPPING).length,
      backend: tf.getBackend()
    };
  }
}

// Create singleton instance
const aiModelService = new AIModelService();

export default aiModelService;
export { COCO_TO_PROP_MAPPING, HISTORICAL_KEYWORDS };